/// <reference path="../../../../../../shared/utils/View.ts"/>
/// <reference path="../../../app.d.ts"/>

import { AppViewService } from '../../services/AppViewService';
import { AppPersistenceService } from '../../services/AppPersistenceService';
import { AppValidationService } from '../../services/AppValidationService';
import { AppErrorService } from '../../services/AppErrorService';
import { AppApplicationService } from '../../services/AppApplicationService';
import { CheckPageController } from "./CheckPageController";
import { AppModelAdapter } from '../../helper/AppModelAdapter';
import { User } from "../../../../../../shared/models/api/User/User";
import { BankAccountService } from '../../../../../../shared/services/BankAccountService';
import { AppApiService } from '../../services/AppApiService';
import { AppRateService } from '../../services/AppRateService';
import { SimulationTypeApi } from '../../../../../../shared/models/generated/TypesSimulationTypeApi';
import { CooperationService } from "../../../../../../shared/services/CooperationService";
import { LegalCommercialService } from '../../../../../../shared/services/LegalCommercialService';
import { EcommerceProductStatus } from '../../../../../../shared/enums/EcommerceProductStatus';
import { AppFormModel } from '../../models/AppFormModel';
import { AppApplicationModel } from '../../models/AppApplicationModel';
import { ApplicationPages } from '../../Constants';
import { AppUtils } from '../../../../../../shared/utils/index';

export class InputPageController implements StepController, InputPageViewEvents {
	private readonly parent: AppController;
	private readonly options: MglModularSystemV2AppOptions;
	private readonly loader: BasicLoader<MglModularSystemV2AppDefinition>;
	private readonly user: User;
	private readonly appPersistenceService: AppPersistenceService;
	private readonly appViewService: AppViewService;
	private appValidationService: AppValidationService;
	private readonly bankAccountService: BankAccountService;
	private readonly appApiService: AppApiService;
	private readonly appErrorService: AppErrorService;
	private readonly cooperationService: CooperationService<MglModularSystemV2AppDefinition>;
	private readonly appApplicationService: AppApplicationService;
	private readonly legalCommercialService: LegalCommercialService;
	private readonly appRateService: AppRateService;
	private readonly priorityUserVisible = "user-visible";
	private streetCheckAccomplished = false;

	constructor(parent: AppController, options: MglModularSystemV2AppOptions, loader: BasicLoader<MglModularSystemV2AppDefinition>, user: User) {
		this.parent = parent;
		this.options = options;
		this.loader = loader;
		this.user = user;
		this.appPersistenceService = new AppPersistenceService(options);
		this.appViewService = new AppViewService(options, loader);
		this.appApiService = new AppApiService(options);
		this.appRateService = new AppRateService(options);
		this.appErrorService = new AppErrorService(options);
		this.appApplicationService = new AppApplicationService(options);
		this.bankAccountService = new BankAccountService(options, user);
		this.legalCommercialService = new LegalCommercialService(options);
		if (this.options.app.iscooperation) {
			this.cooperationService = new CooperationService(options);
		}
	}

	public start(): void {
		const viewConfig = {
			data: this.getViewOptions(),
			templateurl: this.options.app.currenttemplate.templateurl,
			campaignurls: this.options.app.campaignurls,
			parent: this,
			$ctx: this.options.$ctx,
			options: this.options,
			callback: this.afterRenderApp
		};

		this.appViewService.render(viewConfig);
	}

	public rateChangeListener(): void {
		AppUtils.Helper.scheduleTask(() => {
			this.appRateService.updateRate();
		}, this.priorityUserVisible);
	}

	/**
	 * Eventlistener for birthdate change
	 */
	public onBirthdateChange(): void {
		this.legalCommercialService.checkBirthdate(this.appViewService.DateofbirthSelector);
	}

	/**
	 * Update options for Handlebars Template Data
	 */
	private getViewOptions(): { [key: string]: any } {
		const mglApplication = this.appPersistenceService.getApplicationModel();

		let hasJuv = false;
		let hasChild = false;
		if (mglApplication.FormModel.Kinder) {
			mglApplication.FormModel.Kinder.forEach(child => {
				if (child.IsJuv && !hasJuv) {
					hasJuv = true;
				} else if (!child.IsJuv && !hasChild) {
					hasChild = true;
				}
			});
		}

		return {
			FormModel: mglApplication.FormModel,
			Tarif: mglApplication.Tarif,
			User: this.user,
			prevurl: this.options.app.prevurl || '',
			IsYoungGenerationRate: AppModelAdapter.isYoungGenerationRate(this.options.app.rate),
			PartnerModel: mglApplication.FormModel.Partner,
			HidePartnerHeadline: true,
			HasJuv: hasJuv,
			HasChild: hasChild
		};
	}

	public afterRenderApp(): void {
		this.appViewService.setConfigAutocompleteAddress();
		this.loader.loadSubApps({ options: this.options }, () => {
			const formModel = this.appPersistenceService.getFormModelFromStore();
			if (this.options.app.iscooperation) {
				this.cooperationService.initCooperationViewService();
				this.options.app.validatorRules = this.cooperationService.mergeValidatorRules(this.options.app.validatorRules);
			}

			if (this.appApplicationService.isPredate(this.options.app.rate)) {
				this.enablePredate();
			}


			this.parent.afterRenderApp();

			this.legalCommercialService.checkBirthdate(this.appViewService.DateofbirthSelector);
			this.appValidationService = new AppValidationService({ options: this.options }, $(this.options.$ctx).find(`#${this.options.app.applicationId} form`));
			this.appViewService.addInputpageViewListeners(this);
			this.appRateService.updateRate();

			AppUtils.Helper.scheduleTask(() => {
				this.appApiService.getProcessId("mglbaukasten", this.onProcessIdSuccess.bind(this), this.onProcessIdError.bind(this));
				const appModel = this.appPersistenceService.getApplicationModel();
				this.pushEcommerceBasisPacket(appModel, T.Utils.Tracking.GA4_ONLY);
			}, "background");

			this.CheckUrlHasValidSalesmannNumber(formModel);
			this.CheckUrlHasValidAdvertisingKey(formModel);
			if (formModel.Bonus.Bonuscode) {
				this.appValidationService.validateBonuscode(formModel.Bonus.Bonuscode,
					() => {
						this.appViewService.enableBonusEmailField();
					}, this.handleCheckBonuscodeError.bind(this), this.handleCheckBonuscodeError.bind(this));
			}
		});
	}

	private onProcessIdSuccess(data: ApiResponse<string>): void {
		const appModel = this.appPersistenceService.getApplicationModel();
		if (data && data.Data) {
			appModel.ProcessId = parseInt(data.Data);
		} else {
			appModel.ProcessId = 0;
		}
		this.appPersistenceService.setApplicationModel(appModel);
	}

	private onProcessIdError(): void {
		const appModel = this.appPersistenceService.getApplicationModel();
		appModel.ProcessId = 0;
		this.appPersistenceService.setApplicationModel(appModel);
	}

	public onFamilyMemberAdded(): void {
		AppUtils.Helper.scheduleTask(() => {
			this.appValidationService.updateValidationMessages();
		}, this.priorityUserVisible);
	}

	public rerenderChildren(): void {
		const formModel = this.appPersistenceService.getFormModelFromStore();
		if (formModel.Kinder) {
			let isFirstChild = true;
			let isFirstJuv = true;
			for (const child of formModel.Kinder) {
				if (child.IsJuv && isFirstJuv) {
					isFirstJuv = false;
					continue;
				}
				if (!child.IsJuv && isFirstChild) {
					isFirstChild = false;
					continue;
				}
				if (child.IsJuv) {
					S.Utils.Clone.cloneBlock(document.querySelectorAll('.js-add-juv')[0]);
				} else {
					S.Utils.Clone.cloneBlock(document.querySelectorAll('.js-add-child')[0]);
				}
			}
			let juvCount = 0;
			let childCount = 0;
			for (const child of formModel.Kinder) {
				if (child.IsJuv) {
					this.appViewService.setChildDataToViewV2(child, `adultchild_${juvCount}`);
					juvCount++;
				} else {
					this.appViewService.setChildDataToViewV2(child, `child_${childCount}`);
					childCount++;
				}
			}
		}
	}

	public onChildBirthdayChanged(ev: Event): void {
		const childId = this.appViewService.getChildIdFromInputChangeEvent(ev);
		const formModel = this.appPersistenceService.getFormModelFromStore();
		formModel.Kinder.forEach(child => {
			if (child.Id === childId) {
				this.appViewService.updateChildSectionInformation(ev.currentTarget as HTMLElement, child);
			}
		});

		this.appPersistenceService.setFormModelToStore(formModel);

		this.rateChangeListener();
	}

	private async pushEcommerceBasisPacket(appModel: AppApplicationModel, options: { sendPiwik: boolean, sendGA4: boolean }): Promise<void> {
		let model = T.Utils.Tracking.getEcommerceModel(this.options.app.ecommercemodel.producttype);
		model = AppModelAdapter.updateEcommerceModel(model, appModel, this.options);

		model.productStatus = EcommerceProductStatus.CHECKOUT;
		model.checkOutStep = "1";

		await T.Utils.Tracking.setEcommerceModel(model);
		T.Utils.Tracking.push(model, T.Utils.Tracking.CHECKOUT, options);
	}

	public onFormSubmit(ev: JQuery.Event): void {
		ev.preventDefault();
		ev.stopPropagation();
		const appModel = this.appPersistenceService.getApplicationModel();
		const formModel = appModel.FormModel;
		if (this.appValidationService.isValid()) {
			if (!this.streetCheckAccomplished && formModel.Antragssteller.Adresse.StrasseHausnummer.match(/[0-9]/g) === null) {
				this.appViewService.showStreetErrorDialog();
				this.streetCheckAccomplished = true;
				return;
			}
			AppUtils.View.startLoader();
			this.canCreateMembership(appModel, formModel);
		} else {
			if (formModel.Bonus) {
				this.appViewService.replaceKooperationstoken();
			}
		}
	}

	private canCreateMembership(appModel: AppApplicationModel, formModel: AppFormModel): void {
		this.pushEcommerceBasisPacket(appModel, T.Utils.Tracking.PIWIK_ONLY);
		this.sendEcommerceEvents();
		if (this.options.app.iscooperation) {
			this.cooperationService.setModelToStore(appModel.ProcessId);
		}

		if (formModel.Bonus.Bonuscode) {
			this.appValidationService.validateBonuscode(formModel.Bonus.Bonuscode, this.handleCheckBonuscodeSuccess.bind(this), this.handleCheckBonuscodeError.bind(this));
		} else {
			this.handleCheckBonuscodeSuccess();
		}
	}

	private handleCheckBonuscodeSuccess(): void {
		this.appViewService.enableBonusEmailField();
		if (this.appValidationService.isValid()) {
			const appModel = this.appPersistenceService.getApplicationModel();
			if (this.options.app.isofficeadvertisingkey && appModel.FormModel.Werbeschluessel === undefined && this.appValidationService.validatorOfficeAdvertisingKeySecondTry(appModel.FormModel.OfficeWerbeschluessel, this.options.$ctx.find('#officeadvertisingkey'))) {
				this.appValidationService.validateOfficeAdvertisingKey(appModel.FormModel.OfficeWerbeschluessel.toString(),
					this.handleOfficeAdvertisingKeySuccess.bind(this), this.handleOfficeAdvertisingKeyError.bind(this));
			}
			else {
				this.handleOfficeAdvertisingKeySuccess(false);
			}
		} else {
			AppUtils.View.stopLoader();
		}
	}

	private handleOfficeAdvertisingKeySuccess(isValid: boolean): void {
		const appModel = this.appPersistenceService.getApplicationModel();
		const bankdaten = this.bankAccountService.getBankApi();

		if (isValid && appModel.FormModel.OfficeWerbeschluessel && this.options.app.isofficeadvertisingkey) {
			appModel.FormModel.Werbeschluessel = appModel.FormModel.OfficeWerbeschluessel;
			this.appPersistenceService.setApplicationModel(appModel);
		}
		const requestModel = AppModelAdapter.getAnlageApi(appModel.FormModel, null, appModel.Tarif, bankdaten, this.options, false, true);
		const recommendationData = this.appPersistenceService.getRecommendationData();
		if (recommendationData) {
			AppModelAdapter.setRecommendationDataToRequestModel(requestModel, recommendationData);
		}

		const advertisingConsentModel = this.appPersistenceService.getAdvertisingConsent();

		this.appApiService.submitEnclosure(requestModel, advertisingConsentModel, this.submitSuccess.bind(this), this.submitFailed.bind(this), SimulationTypeApi.OnlyValidate);
	}

	private handleOfficeAdvertisingKeyError(): void {
		AppUtils.View.stopLoader();
	}

	private submitSuccess(): void {
		const checkOptions = this.appApplicationService.preparePageOptions(ApplicationPages.Check);
		const checkController = new CheckPageController(this.parent, checkOptions, this.user, this.loader);
		checkController.start();
	}

	public salesmanNoBlurListener(ev: Event): void {
		const formModel = new AppFormModel();
		const field = ev.target as HTMLInputElement;
		formModel.Profiwerbernummer = field.value;
		this.appValidationService.validateSalesmenNo(formModel,
			() => {
				// success: nothing to do
				console.log("salesman validation success");
			}, () => {
				// failure: handled by appValidationService
				console.log("salesman validation fail");
			}, true, field);
	}

	public validateBonuscode(ev: Event): void {
		this.appValidationService.validateBonuscode(undefined, () => {
			this.appViewService.enableBonusEmailField();
		}, () => {
			this.appViewService.replaceKooperationstoken();
		}, ev.target as HTMLInputElement);
	}

	private handleCheckBonuscodeError(): void {
		this.appViewService.replaceKooperationstoken();
		AppUtils.View.stopLoader();
	}

	public submitFailed(jqxhr: JQueryXHR): void {
		if (jqxhr.status === 400) {
			this.appErrorService.handleError(jqxhr);
		} else {
			this.appPersistenceService.clearApplicationSession();
			this.appErrorService.handleTechnicalErrorWithErrorTemplate(jqxhr, true);
		}
		T.Utils.View.stopLoader();
	}

	public onViewChanged(): void {
		AppUtils.Helper.scheduleTask(() => {
			const appModel = this.appPersistenceService.getApplicationModel();
			const formModel = appModel.FormModel;
			formModel.Antragssteller = this.appViewService.getPersonalDataModel();
			formModel.Partner = this.appViewService.getPartnerDataModel();
			formModel.Kinder = this.appViewService.getChildDataModel();
			this.appViewService.updateOfficeAdvertisingKey(formModel);
			this.appViewService.updateBonusValues(formModel);
			this.appViewService.updateDiscountValues(formModel);
			this.appViewService.updateLegalValues(formModel);
			this.appViewService.updateSalesmanFields(formModel);

			formModel.MitgliedschaftStartDatum = this.appViewService.getStartOfMembershipDate();
			formModel.BisherigeMitgliedsnummer = this.appViewService.getBisherigeMitgliedsnummer();

			this.appPersistenceService.setFormModelToStore(formModel);

			if (this.options.app.iscooperation) {
				this.cooperationService.setModelToStore(appModel.ProcessId);
			}
		}, this.priorityUserVisible);

	}

	private enablePredate(): void {
		if (this.appViewService.GetPreDate() === 'Vordatierung 2 Monate') {
			this.options.app.validatorRules.rules.MitgliedschaftStartDatum['startofmembershippredate2m'] = true;
		}
		else {
			this.options.app.validatorRules.rules.MitgliedschaftStartDatum['startofmembershippredate6m'] = true;
		}
	}

	private CheckUrlHasValidSalesmannNumber(formModel: AppFormModel): void {
		const salesmannumberfromurl = this.options.app.salesmannumberfromurl;
		if (salesmannumberfromurl && salesmannumberfromurl.length > 0) {
			const formModelTemp = formModel;
			formModelTemp.Profiwerbernummer = salesmannumberfromurl;
			this.appViewService.hideSalesmanFiels();
			this.appValidationService.validateSalesmenNo(formModelTemp,
				() => {
					formModel.Profiwerbernummer = salesmannumberfromurl;
					formModel.Pruefziffer = 0;
					this.appPersistenceService.setFormModelToStore(formModel);
				}, () => {
					formModel.Profiwerbernummer = "";
				}, false, null);
		}
	}

	private CheckUrlHasValidAdvertisingKey(formModel: AppFormModel): void {
		const gsWbs = T.Utils.Helper.getUrlParameter('gswbs');
		if (gsWbs && gsWbs.length > 0) {
			this.appApiService.checkMglWbs(gsWbs,
				() => {
					formModel.Werbeschluessel = gsWbs;
					this.appPersistenceService.setFormModelToStore(formModel);
				}, () => {
					formModel.Werbeschluessel = Number(this.options.app.advertisingkey);
					this.appPersistenceService.setFormModelToStore(formModel);
				});
		}
		else {
			formModel.Werbeschluessel = undefined;
			this.appPersistenceService.setFormModelToStore(formModel);
		}
	}

	public async pagehideListener(): Promise<void> {
		await this.sendEcommerceEvents();
	}

	protected async sendEcommerceEvents(): Promise<void> {
		const appModel = this.appPersistenceService.getApplicationModel();
		const isAddressComplete = this.appValidationService.validatorCompleteAddress({
			streetnumber: appModel.FormModel.Antragssteller.Adresse.StrasseHausnummer,
			zip: appModel.FormModel.Antragssteller.Adresse.Postleitzahl,
			city: appModel.FormModel.Antragssteller.Adresse.Ort,
		});

		let model = T.Utils.Tracking.getEcommerceModel(this.options.app.ecommercemodel.producttype);
		model = AppModelAdapter.updateEcommerceModel(model, appModel, this.options);
		await T.Utils.Tracking.setEcommerceModel(model);

		const isAddressValid = this.appValidationService.isSelectorValid('.js-event-valid input');
		if (isAddressComplete && isAddressValid) {
			await T.Utils.Tracking.push(model, T.Utils.Tracking.EVENTS.ADD_SHIPPING_INFO, T.Utils.Tracking.GA4_ONLY);
		}
		const bankdatenModel = this.bankAccountService.getBankdaten();
		if (bankdatenModel.Iban && bankdatenModel.Iban !== '') {
			await T.Utils.Tracking.push(model, T.Utils.Tracking.EVENTS.ADD_PAYMENT_INFO, T.Utils.Tracking.GA4_ONLY);
		}
	}
}
